import { useState, useEffect, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { supabase } from '../supabaseClient';
import type { ShowToast, ShowConfirm, FetchData } from '../types/appShared';

// ══════════════════════════════════════════════
// useTasksController — كل الـ state و الـ handlers الخاصة بصفحة Tasks
// (الفلاتر، الترتيب، صفحات الجدول، مودال الإضافة/التعديل، مودال التفاصيل،
// التحديات) كانت كلها متفرّقة جوه App.jsx (~15 useState و9 functions متوزّعين
// على أكتر من 2000 سطر، مخلوطين مع state صفحات تانية زي Schedule/Attendance/
// Comments). نقلهم هنا كـ hook واحد مستقل عشان:
//   1) App.jsx يصغر ويوضح (الكومبوننت الرئيسي مش محتاج يعرف تفاصيل إدارة
//      تاسكات واحدة واحدة).
//   2) الكود ده يبقى reusable/testable لوحده بدون الحاجة لتشغيل App.jsx كله.
//
// ملاحظة مهمة: tasksData/setTasksData و taskChallenges/setTaskChallenges
// اتسابوا في App.jsx (مش هنا) لأنهم بيتحمّلوا من fetchData() المركزية اللي
// بتجيب بيانات المشروع كله مرة واحدة (مش خاصين بصفحة Tasks فقط) — فبيتبعتوا
// هنا كـ parameters بدل ما يتكرر تعريفهم.
// ══════════════════════════════════════════════
export interface Task {
    id: string | number;
    system: string;
    sub_task: string;
    assigned_to: string | null;
    status: string;
    priority: string;
    start_date: string | null;
    deadline_date: string | null;
    notes: string | null;
    tags?: string[];
    progress?: number;
    task_value?: number;
    latitude?: number | null;
    longitude?: number | null;
    location_name?: string | null;
    baseline_start?: string | null;
    baseline_end?: string | null;
    created_at?: string;
    updated_at?: string;
    // ✅ بيتحدّث بس لما progress يتغيّر فعليًا (DB trigger) — مش أي تعديل تاني
    // على التاسك زي updated_at. مستخدم في Progress Report عشان "تحديثات"
    // تعكس تقدم حقيقي بدل أي حفظ.
    progress_updated_at?: string;
    // ✅ true لما التاسك يوصل 100% (عبر الأنشطة اليومية) بس لسه محتاج تأكيد
    // المسؤول (PM) بزرار مباشر قبل ما status يتسجل فعليًا 'Completed'.
    completion_pending_confirmation?: boolean;
}

interface TaskForm {
    system: string;
    sub_task: string;
    assigned_to: string;
    progress: number | string;
    status: string;
    priority: string;
    start_date: string;
    deadline_date: string;
    notes: string;
    tags: string[];
    task_value: string | number;
    latitude?: number | null;
    longitude?: number | null;
    location_name?: string;
}

interface ChallengeForm {
    description: string;
    severity: string;
    imageFile: File | null;
}

interface UseTasksControllerParams {
    currentProjectId: string | null;
    setTasksData: Dispatch<SetStateAction<Task[]>>;
    setTaskChallenges: Dispatch<SetStateAction<Record<string, any[]>>>;
    fetchData: FetchData;
    showToast: ShowToast;
    showConfirm: ShowConfirm;
}

export function useTasksController({
    currentProjectId,
    setTasksData,
    setTaskChallenges,
    fetchData,
    showToast,
    showConfirm,
}: UseTasksControllerParams) {
    // ── فلاتر/بحث/ترتيب ──
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");
    const [filterOwner, setFilterOwner] = useState("All");
    const [filterSystem, setFilterSystem] = useState('All');
    const [sortBy, setSortBy] = useState('deadline'); // 'deadline', 'priority', 'progress', 'status'
    const [sortOrder, setSortOrder] = useState('asc');

    // ── Table/Kanban view + ترقيم صفحات ──
    const [tasksView, setTasksView] = useState('table'); // 'table' or 'kanban'
    // ترقيم صفحات جدول التاسكات — بدل ما نرندر الـ 608 صف كلهم مرة واحدة،
    // بنعرض صفحة بـ 50 صف بس في كل مرة.
    const [tasksPage, setTasksPage] = useState(1);
    const [selectedTasks, setSelectedTasks] = useState<(string | number)[]>([]);

    // كل ما فلاتر/بحث التاسكات يتغيّروا، نرجع لصفحة 1 (عشان ميحصلش إن المستخدم
    // واقف على صفحة 3 مثلاً وبعد الفلترة يبقى مفيش نتائج تظهر أصلاً).
    useEffect(() => {
        setTasksPage(1);
    }, [searchTerm, filterStatus, filterOwner, filterSystem]);

    // ── مودال الإضافة/التعديل ──
    const [showAddTask, setShowAddTask] = useState(false);
    const [editingTask, setEditingTask] = useState<Task | null>(null);
    const [taskForm, setTaskForm] = useState<TaskForm>({
        system: '',
        sub_task: '',
        assigned_to: '',
        progress: 0,
        status: 'Pending',
        priority: 'Medium',
        start_date: '',
        deadline_date: '',
        notes: '',
        tags: [],
        task_value: '', // ✅ قيمة التاسك بالريال — منها بيتحسب الـ Weight تلقائي، ومنها EV/PV/BAC
    });
    const [savingTask, setSavingTask] = useState(false);

    // ── مودال التفاصيل + التحديات ──
    const [showTaskDetails, setShowTaskDetails] = useState<Task | null>(null);
    const [taskDetailTab, setTaskDetailTab] = useState('details');
    const [challengeForm, setChallengeForm] = useState<ChallengeForm>({ description: '', severity: 'Medium', imageFile: null });
    const [uploadingChallenge, setUploadingChallenge] = useState(false);

    // عند فتح task details
    const loadChallenges = useCallback(async (taskId: string | number) => {
        const { data } = await supabase
            .from('task_challenges')
            .select('*')
            .eq('task_id', taskId)
            .order('created_at', { ascending: false });
        setTaskChallenges(prev => ({ ...prev, [taskId]: data || [] }));
    }, [setTaskChallenges]);

    const handleSaveTask = async () => {
        if (!taskForm.sub_task || !taskForm.system) {
            showToast('Please fill in Task Name and System');
            return;
        }

        setSavingTask(true);

        try {
            const taskData: Record<string, any> = {
                project_id: currentProjectId,
                system: taskForm.system,
                sub_task: taskForm.sub_task,
                assigned_to: taskForm.assigned_to || null,
                status: taskForm.status,
                priority: taskForm.priority,
                start_date: taskForm.start_date || null,
                deadline_date: taskForm.deadline_date || null,
                notes: taskForm.notes || null,
                updated_at: new Date().toISOString(),
                latitude: taskForm.latitude || null,
                longitude: taskForm.longitude || null,
                location_name: taskForm.location_name || null,
                progress: taskForm.status === 'Completed' ? 100 : parseInt(String(taskForm.progress)) || 0,
                task_value: (taskForm.task_value !== '' && taskForm.task_value != null && !isNaN(parseFloat(String(taskForm.task_value)))) ? parseFloat(String(taskForm.task_value)) : 0,
            };

            if (editingTask) {
                // Update existing — الـ baseline (baseline_start/baseline_end) عمدًا مش
                // موجودة هنا: أول ما تتسجل بتتجمد ومتتحرّكش مع أي reschedule بعد كده،
                // عشان الـ PV تفضل بتتحسب من خطة ثابتة زي أي EVM احترافي، مش من التواريخ
                // الفعلية المتغيّرة.
                const { error } = await supabase
                    .from('tasks')
                    .update(taskData)
                    .eq('id', editingTask.id);

                if (error) throw error;
                showToast('✅ Task updated successfully!');
            } else {
                // Create new — نجمّد الـ baseline من تواريخ الإنشاء نفسها (أول مرة بس)
                taskData.created_at = new Date().toISOString();
                taskData.baseline_start = taskForm.start_date || null;
                taskData.baseline_end = taskForm.deadline_date || null;
                const { error } = await supabase
                    .from('tasks')
                    .insert([taskData]);

                if (error) throw error;
                showToast('✅ Task created successfully!');
            }

            // Reset & Close أولاً
            setTaskForm({
                system: '', sub_task: '', assigned_to: '', progress: 0,
                status: 'Pending', priority: 'Medium', start_date: '',
                deadline_date: '', notes: '', tags: [],
                latitude: null, longitude: null, location_name: '',
                task_value: '',
            });
            setShowAddTask(false);
            setEditingTask(null);
            setSavingTask(false);

            // Refresh في الخلفية
            fetchData(currentProjectId);

        } catch (error: any) {
            console.error('Error saving task:', error);
            showToast('❌ Failed to save task. Please try again.');
        } finally {
            setSavingTask(false);
        }
    };

    // ثابتين (useCallback) عشان TaskRow المغلّف بـ React.memo يستفيد فعلياً
    // حتى وانت بتكتب في خانة البحث بتاعة صفحة التاسكات.
    const handleToggleTaskSelect = useCallback((taskId: string | number, checked: boolean) => {
        setSelectedTasks(prev => checked ? [...prev, taskId] : prev.filter(id => id !== taskId));
    }, []);

    const handleOpenTaskDetails = useCallback((t: Task) => {
        setShowTaskDetails(t);
        // ✅ تصفير التاب هنا بالظبط (لحظة الفتح) مش جوه loadChallenges —
        // كان قبل كده جوه loadChallenges، يعني كل ما طلب الشبكة يخلص (بعد
        // ما المودال يكون فاتح بالفعل) كان بيرجّع التاب لـ "Details" تلقائي،
        // حتى لو المستخدم كان خلاص دوس على تاب "Challenges" قبل ما الطلب
        // يخلص — ده كان بيبان "رجفة"/قفزة محتوى مفاجئة من غير ما المستخدم
        // يعمل حاجة. دلوقتي التصفير بيحصل مرة واحدة بس وقت الفتح.
        setTaskDetailTab('details');
        loadChallenges(t.id);
    }, [loadChallenges]);

    const handleEditTask = useCallback((task: Task) => {
        setEditingTask(task);
        setTaskForm({
            system: task.system || '',
            sub_task: task.sub_task || '',
            assigned_to: task.assigned_to || '',
            progress: task.progress || 0,
            status: task.status || 'Pending',
            priority: task.priority || 'Medium',
            start_date: task.start_date || '',
            deadline_date: task.deadline_date || '',
            notes: task.notes || '',
            tags: task.tags || [],
            latitude: null,
            longitude: null,
            location_name: '',
            task_value: task.task_value ?? '',
        });
        setShowAddTask(true);
    }, []);

    // ✅ تحديث قيمة التاسك مباشرة من جدول التاسكات (inline)، من غير فتح المودال —
    // بديل لتعديله يدويًا من جدول Supabase. الـ Weight% بيتحسب تلقائي من القيم دي
    // (مش عمود منفصل بيتعدّل)، فمش محتاجين نحدّث حاجة تانية غير task_value.
    // Optimistic update + تصحيح تلقائي لو فشل.
    const handleUpdateTaskValue = useCallback(async (taskId: string | number, newValue: string | number) => {
        const safeValue = isNaN(parseFloat(String(newValue))) ? 0 : parseFloat(String(newValue));
        setTasksData(prev => prev.map(t => t.id === taskId ? { ...t, task_value: safeValue } : t));
        try {
            const { error } = await supabase
                .from('tasks')
                .update({ task_value: safeValue, updated_at: new Date().toISOString() })
                .eq('id', taskId);
            if (error) throw error;
        } catch (error: any) {
            console.error('Error updating task value:', error);
            showToast('❌ Failed to update task value. Please try again.');
            fetchData(currentProjectId); // رجّع القيمة الصح من السيرفر لو فشل
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProjectId]);

    const handleDeleteTask = useCallback(async (taskId: string | number) => {
        // ✅ الرسالة كانت بالغلط بتقول "this expense" (نسخ-لصق من handler تاسك
        // تاني) بدل "this task" — صلّحتها.
        showConfirm('Are you sure you want to delete this task?', async () => {
            try {
                const { error } = await supabase
                    .from('tasks')
                    .delete()
                    .eq('id', taskId);

                if (error) throw error;

                await fetchData(currentProjectId);
                showToast('✅ Task deleted successfully!');
            } catch (error: any) {
                console.error('Error deleting task:', error);
                showToast('❌ Failed to delete task. Please try again.');
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData مش memoized وبتتغيّر كل render؛ إضافتها هنا هتعيد إنشاء الـ callback من غير فايدة. النطاق المقصود هو إعادة الإنشاء عند تغيّر currentProjectId فقط
    }, [currentProjectId]);

    const handleBulkDelete = () => {
        showConfirm(`Are you sure you want to delete ${selectedTasks.length} selected tasks? This action cannot be undone.`, async () => {
            try {
                const { error } = await supabase
                    .from('tasks')
                    .delete()
                    .in('id', selectedTasks);

                if (error) throw error;

                await fetchData(currentProjectId);
                setSelectedTasks([]);
                showToast('✅ Tasks deleted successfully!');
            } catch (error: any) {
                console.error('Error deleting tasks:', error);
                showToast('❌ Failed to delete tasks. Please try again.');
            }
        });
    };

    const getSortedTasks = (tasks: Task[]) => {
        return [...tasks].sort((a, b) => {
            let aVal: number | Date, bVal: number | Date;

            switch (sortBy) {
                case 'deadline':
                    aVal = a.deadline_date ? new Date(a.deadline_date) : new Date('9999-12-31');
                    bVal = b.deadline_date ? new Date(b.deadline_date) : new Date('9999-12-31');
                    break;
                case 'priority': {
                    const priorityOrder: Record<string, number> = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };
                    aVal = priorityOrder[a.priority] || 0;
                    bVal = priorityOrder[b.priority] || 0;
                    break;
                }
                case 'progress':
                    aVal = a.progress || 0;
                    bVal = b.progress || 0;
                    break;
                case 'task_value':
                    aVal = parseFloat(String(a.task_value)) || 0;
                    bVal = parseFloat(String(b.task_value)) || 0;
                    break;
                case 'status': {
                    const statusOrder: Record<string, number> = { 'Completed': 3, 'In Progress': 2, 'Pending': 1 };
                    aVal = statusOrder[a.status] || 0;
                    bVal = statusOrder[b.status] || 0;
                    break;
                }
                default:
                    return 0;
            }

            return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
        });
    };

    return {
        searchTerm, setSearchTerm, filterStatus, setFilterStatus, filterOwner, setFilterOwner, filterSystem, setFilterSystem,
        sortBy, setSortBy, sortOrder, setSortOrder,
        tasksView, setTasksView, tasksPage, setTasksPage,
        selectedTasks, setSelectedTasks, handleToggleTaskSelect, handleBulkDelete,
        showAddTask, setShowAddTask, taskForm, setTaskForm, editingTask, setEditingTask, savingTask, handleSaveTask,
        showTaskDetails, setShowTaskDetails, handleOpenTaskDetails, handleEditTask, handleDeleteTask,
        handleUpdateTaskValue,
        taskDetailTab, setTaskDetailTab,
        challengeForm, setChallengeForm, uploadingChallenge, setUploadingChallenge, loadChallenges,
        getSortedTasks,
    };
}

export default useTasksController;
